import type { SelectChangeEvent } from '@mui/material';
import {
  Button,
  FormControlLabel,
  FormLabel,
  Input,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
} from '@mui/material';
import React, { useLayoutEffect, useRef } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useDebounceCallback } from '@/hooks';

import { Sections } from './components/Sections';
import { Textarea } from './components/Textarea';
import { Video } from './components/Video';
import { VideoContainer } from './components/VideoContainer';

const renderError = (e: any) => {
  if (e instanceof Error) {
    return `${e.name}: ${e.message}`;
  }
  console.log(e);
  return `Unknown error. Check console`;
};

type Range = (DoubleRange | ULongRange) & { step?: number };

const isRange = (range: unknown): range is Range =>
  typeof range === 'object' &&
  range !== null &&
  ('min' in range || 'max' in range);

const capabilitiesOverride: Record<string, number[]> = {
  aspectRatio: [16 / 9, 4 / 3, 1, 3 / 4, 9 / 16],
};

export const TestCam: React.FC = () => {
  const [log, setLog] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addToLog = useCallback((record: string) => {
    setLog((prev) => [...prev, record]);
  }, []);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [log]);

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const [capabilities, setCapabilities] = useState<MediaTrackCapabilities>({});
  const [constraints, setConstraints] = useState<MediaTrackConstraints>({});

  useEffect(() => {
    addToLog(JSON.stringify(navigator.mediaDevices.getSupportedConstraints()));
  }, [addToLog]);

  useEffect(() => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        setDevices(devices.filter((device) => device.kind === 'videoinput'));
      })
      .catch((e) => addToLog(`enumerateDevices error: ${renderError(e)}`));
  }, [addToLog]);

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    setDeviceId(event.target.value);
  }, []);

  const getUserMedia = useCallback(
    () => {
      const constraintsWithDeviceId = { video: { deviceId, ...constraints } };
      addToLog(`getUserMedia with ${JSON.stringify(constraintsWithDeviceId)}`);

      navigator.mediaDevices
        .getUserMedia(constraintsWithDeviceId)
        .then(setStream)
        .catch((e) => addToLog(`getUserMedia error: ${renderError(e)}`));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceId, addToLog],
  );

  useEffect(() => {
    getUserMedia();
  }, [getUserMedia]);

  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((track) => {
        addToLog(`stop track ${stream.id}:${track.id}`);
      });
    };
  }, [addToLog, stream]);

  useEffect(() => {
    if (!stream) {
      return;
    }

    addToLog(`Stream ready ${stream.id}`);

    const track = stream.getVideoTracks()[0];

    if (!track) {
      addToLog('Not found at least one video track');
      return;
    }

    setTrack(track);
    const settings = track.getSettings();
    setConstraints(settings);

    addToLog(`Video track settings: ${JSON.stringify(settings)}`);

    setCapabilities(track.getCapabilities());
  }, [addToLog, stream]);

  useEffect(() => {
    addToLog(`Capabilities: ${JSON.stringify(capabilities)}`);
  }, [addToLog, capabilities]);

  const handleApplyConstraint = useDebounceCallback(
    (constraint: Record<string, unknown>) => {
      addToLog(`Apply constraint: ${JSON.stringify(constraint)}`);
      track
        ?.applyConstraints(constraint)
        .then(() => {
          addToLog(`Constraint applied: ${JSON.stringify(constraint)}\``);
        })
        .catch((e) =>
          addToLog(
            `applyConstraints error: ${renderError(e)}: ${JSON.stringify(
              constraint,
            )}`,
          ),
        );
    },
    [track],
  );

  const breakCamera = useCallback(() => {
    if (typeof capabilities.brightness?.min === 'number') {
      track?.applyConstraints({ brightness: capabilities.brightness?.min });
    }
    if (typeof capabilities.contrast?.max === 'number') {
      track?.applyConstraints({ contrast: capabilities.contrast?.max });
    }

    const manualExposureMode = capabilities.exposureMode?.find((mode) =>
      /manual/i.test(mode),
    );
    if (manualExposureMode) {
      track?.applyConstraints({
        exposureMode: manualExposureMode,
      });
    }
    if (typeof capabilities.exposureTime?.min === 'number') {
      track?.applyConstraints({ exposureTime: capabilities.exposureTime?.min });
    }
  }, [capabilities, track]);

  const fixCamera = useCallback(() => {
    if (
      typeof capabilities.brightness?.min === 'number' &&
      typeof capabilities.brightness?.max === 'number'
    ) {
      track?.applyConstraints({
        brightness:
          (capabilities.brightness.max - capabilities.brightness.min) / 2,
      });
    }
    if (
      typeof capabilities.contrast?.min === 'number' &&
      typeof capabilities.contrast?.max === 'number'
    ) {
      track?.applyConstraints({
        contrast: (capabilities.contrast.max - capabilities.contrast.min) / 2,
      });
    }

    const continuousExposureMode = capabilities.exposureMode?.find((mode) =>
      /cont/i.test(mode),
    );
    if (continuousExposureMode) {
      track?.applyConstraints({
        exposureMode: continuousExposureMode,
      });
    }
  }, [capabilities, track]);

  const handleChangeConstraint = useCallback(
    (key: string, value: unknown) => {
      const constraint = { [key]: value };
      setConstraints((constraints) => ({ ...constraints, ...constraint }));
      handleApplyConstraint(constraint);
    },
    [handleApplyConstraint],
  );

  const videoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      if (node) {
        node.srcObject = stream;
      }
    },
    [stream],
  );

  return (
    <>
      <VideoContainer>
        <Video ref={videoRef} controls autoPlay playsInline />
      </VideoContainer>
      <Sections>
        <Select value={deviceId} label="Device id" onChange={handleChange}>
          {devices.map(({ deviceId, label }) => (
            <MenuItem key={deviceId} value={deviceId}>
              {label || deviceId}
            </MenuItem>
          ))}
        </Select>
        <Button onClick={getUserMedia}>Get user media</Button>
        <Button onClick={breakCamera}>Break camera</Button>
        <Button onClick={fixCamera}>Fix camera</Button>

        {Object.entries(capabilities).map(([key, browserCapability]) => {
          const capability =
            key in capabilitiesOverride
              ? capabilitiesOverride[key]
              : browserCapability;

          const value = constraints[key as keyof typeof constraints];

          if (capability instanceof Array && capability.length) {
            return (
              <div key={key}>
                <FormLabel>{key}</FormLabel>
                <RadioGroup
                  key={key}
                  value={value}
                  defaultValue={capability[0]}
                  onChange={(event) =>
                    handleChangeConstraint(key, event.target.value)
                  }
                >
                  {capability.map((item) => (
                    <FormControlLabel
                      value={item}
                      control={<Radio />}
                      label={item}
                      key={item}
                    />
                  ))}
                </RadioGroup>
              </div>
            );
          }

          if (capability instanceof Array && capability.length) {
            return (
              <div key={key}>
                <FormLabel>{key}</FormLabel>
                <RadioGroup
                  key={key}
                  value={value}
                  defaultValue={capability[0]}
                  onChange={(event) =>
                    handleChangeConstraint(key, event.target.value)
                  }
                >
                  {capability.map((item) => (
                    <FormControlLabel
                      value={item}
                      control={<Radio />}
                      label={item}
                      key={item}
                    />
                  ))}
                  <Button
                    onClick={() => handleChangeConstraint(key, undefined)}
                  >
                    Reset
                  </Button>
                </RadioGroup>
              </div>
            );
          }
          if (isRange(capability)) {
            return (
              <div key={key}>
                <FormLabel>{key}</FormLabel>
                <Input
                  value={value || ''}
                  onChange={(e) => handleChangeConstraint(key, e.target.value)}
                />
                <Slider
                  value={(value as number | undefined) || capability.min || 0}
                  min={capability.min}
                  max={capability.max}
                  step={capability.step}
                  key={key}
                  onChange={(_, value) => {
                    handleChangeConstraint(key, value);
                  }}
                />
                <Button onClick={() => handleChangeConstraint(key, undefined)}>
                  Reset
                </Button>
              </div>
            );
          }
        })}
        <Textarea value={log.join('\n\n')} rows={10} ref={textareaRef} />
      </Sections>
    </>
  );
};
